/**
 * Renders a stack of overlapping product logo icons for the given source URLs.
 * Deduplicates logos and displays up to 3, with a "+X" overflow count if there
 * are more unique source types than can be displayed.
 */
import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getLogoForSourceType, getSourceType } from './source-util';

export const MAX_VISIBLE_LOGOS = 3;

const styles = cssMap({
	outerContainer: {
		display: 'inline-flex',
		alignItems: 'center',
	},
	overflowCount: {
		marginInlineEnd: token('space.050'),
	},
	container: {
		display: 'inline-flex',
		alignItems: 'center',
		position: 'relative',
	},
	logoWrapper: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		marginInlineStart: token('space.negative.100'),
	},
	firstLogo: {
		marginInlineStart: token('space.negative.025'),
	},
	logo: {
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width.selected'),
		borderStyle: 'solid',
		width: '20px',
		height: '20px',
		borderColor: token('color.border.inverse'),
	},
});

interface SourceLogoStackProps {
	sources: string[];
	/**
	 * When true, suppresses the built-in "+N" overflow indicator. Use this when the
	 * consumer renders its own combined count label (e.g. "+57 Sources") next to
	 * the stack and a duplicated "+N" would be confusing.
	 * @default false
	 */
	hideOverflowCount?: boolean;
}

/** Renders overlapping product logos derived from an array of source URLs, with +X overflow. */
export const SourceLogoStack = ({
	sources,
	hideOverflowCount = false,
}: SourceLogoStackProps): React.JSX.Element | null => {
	if (sources.length === 0) {
		return null;
	}

	const allUniqueSourceTypes = Array.from(new Set(sources.map(getSourceType)));
	const visibleSourceTypes = allUniqueSourceTypes.slice(0, MAX_VISIBLE_LOGOS);
	const overflowCount = sources.length - visibleSourceTypes.length;

	return (
		<Box xcss={styles.outerContainer}>
			<Box xcss={styles.container}>
				{visibleSourceTypes.map((sourceType, index) => {
					const Logo = getLogoForSourceType(sourceType);
					return (
						<Box
							key={sourceType}
							xcss={cx(styles.logoWrapper, index === 0 && styles.firstLogo)}
							style={{ zIndex: visibleSourceTypes.length - index }}
						>
							<Box xcss={styles.logo}>
								<Logo size="xxsmall" shouldUseNewLogoDesign />
							</Box>
						</Box>
					);
				})}
			</Box>
			{!hideOverflowCount && overflowCount > 0 && (
				<Box xcss={styles.overflowCount}>
					<Text size="small" color="color.text.subtlest">
						+{overflowCount}
					</Text>
				</Box>
			)}
		</Box>
	);
};
